
import Vue from 'vue';
import { Component, Emit, InjectReactive, Prop } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

const FreemiumFilterBadge = () =>
	import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue');

import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/components/search/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { isSportSupported } from '@/enums/sports';
import type { WebLocale } from '@/enums/web-locale';

import Provider from '@/interfaces/provider';
import { HtmlContentMovieDetails, UrlMetadataResponse } from '@/interfaces/responses/url-metadata';

import { GenresByShortNameConstant } from '@/constants/ConstantsProvider.vue';
import { Genres } from '@/constants/types';
import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { isEditorialLocale } from '@/routing/editorial';
import type { FilterCollection } from '@/stores/filter.store';
import { ListsCollectionType } from '@/stores/routing.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const SearchSuggester = namespace('searchSuggester');
const User = namespace('user');

@Component({
	name: 'AppHeader',
	components: {
		FreemiumFilterBadge,
		LoginMenuButton,
		LoginMenuButtonNew,
		SearchSuggesterBar,
		SettingsButton,
	},
})
export default class AppHeader extends Vue {
	CollectionType = CollectionType;

	@Prop({ required: true }) lastActiveCollectionType: null | CollectionType;
	@Prop({ required: true }) locale: string;

	@InjectReactive({ from: GenresByShortNameConstant, default: [] }) readonly genresByShortName: Genres;

	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Filter.Getter currentFilters: FilterCollection;
	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Language.Getter country: string;
	@Language.State detectedWebLocale: WebLocale;
	@Language.State webLocale: WebLocale;

	@Routing.Getter urlMetadata: UrlMetadataResponse<HtmlContentMovieDetails>;
	@Routing.State activeRoute: any;
	@Routing.State lastRoute: Route;
	@Routing.State urlMetadataPromise: any;
	@Routing.State lastActiveListsCollectionType: ListsCollectionType | null;

	@SearchSuggester.Getter isSearchSuggesterOpen: boolean;
	@SearchSuggester.Action setSearchSuggesterVisibility: (value: boolean) => void;

	@User.State onboardingStatus: OnboardingStatus;
	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;

	get userLoggedIn(): boolean {
		return this.isLoggedIn();
	}

	get isNewTab() {
		const newPageRoutes = ['app.titles.new', 'app.titles.upcoming', 'app.titles.pricedrops'];
		return newPageRoutes.find(route => (this.$route.name || '').startsWith(route));
	}

	get isHome(): boolean {
		return this.$route.name === 'app.home';
	}

	get isGlobal(): boolean {
		return this.onboardingStatus === OnboardingStatus.GLOBAL;
	}

	get isUnsupportedCountry(): boolean {
		return this.onboardingStatus === OnboardingStatus.UNSUPPORTED_COUNTRY;
	}

	get isStreamingCharts() {
		return this.$route.name === 'app.streaming-charts';
	}

	get logoSrc() {
		const file = `JustWatch-logo-${this.isGlobal ? 'large' : 'small'}`;
		return {
			png: `/${ASSETS_DIR}/img/logo/${file}.png`,
			webp: `/${ASSETS_DIR}/img/logo/${file}.webp`,
		};
	}

	get hasSports() {
		return isSportSupported(this.webLocale);
	}

	get isValentine() {
		return new Date().getDate() === 14 && new Date().getMonth() === 1;
	}

	get isGuideLocale() {
		return isEditorialLocale(this.webLocale);
	}

	get listsCollectionTypeToNavigate() {
		if (this.lastActiveListsCollectionType) {
			return this.lastActiveListsCollectionType;
		}

		return this.isLoggedIn() ? CollectionType.MY_LISTS : CollectionType.PUBLIC_LISTS;
	}

	/**
	 * with these parameters we can always link to the correct url for each collectionType page.
	 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
	 */
	routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
		const isNewPageCollectionType = [
			CollectionType.COMING_SOON,
			CollectionType.LEAVING_SOON,
			CollectionType.PRICEDROPS,
		].includes(collectionType);

		const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
		const filters = filterOverwrite ?? this.filters(filterCollectionType);
		const routeName = getRouteName(collectionType, filters);
		const params = getUrlParamsFromFilterState(
			routeName,
			filters,
			this.providersByShortName,
			this.genresByShortName,
			collectionType,
			this.$route.query
		);
		return {
			name: routeName,
			...params,
		};
	}

	@Emit('tabChange')
	async onTabChange(newTab: string) {
		await this.urlMetadataPromise;
		const pageTypeContext = await TrackingHelper.getPageTypeContext(this.lastRoute.path, true);
		this.trackEvent('tabchange', newTab, [pageTypeContext]);

		return newTab;
	}

	trackEvent(action: string, label?: string, contexts: SnowplowContext[] = []) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action,
				label,
			},
			contexts
		);
		if (label === 'home') {
			const onboardingStatus =
				this.onboardingStatus === OnboardingStatus.TASTE_ONBOARDING
					? OnboardingStatus.GLOBAL
					: this.onboardingStatus;

			TrackingHelper.trackEvent('home_page_view', {
				action: onboardingStatus,
				property: this.detectedWebLocale || this.webLocale,
			});
		}
	}

	onOverlayClick() {
		this.setSearchSuggesterVisibility(false);
	}
}
